import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "../../data/services";
import Reveal from "../Reveal";

const SERVICE_IMG =
  "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg";

const Card = ({ service, className = "", testid }) => {
  const Icon = service.icon;
  return (
    <Link
      to="/services"
      data-testid={testid}
      className={`group relative flex flex-col bg-white border border-black/10 p-8 hover:border-[#2f4bff] hover:-translate-y-1 hover:shadow-[0_20px_40px_-24px_rgba(17,17,17,0.35)] transition-all duration-300 overflow-hidden ${className}`}
    >
      <div className="flex items-start justify-between">
        <span className="font-mono-tech text-xs text-neutral-400 tracking-widest">
          {service.index}
        </span>
        <Icon className="h-6 w-6 text-[#2f4bff]" />
      </div>
      <div className="mt-10 flex flex-col flex-1">
        <h3 className="font-head text-2xl font-semibold tracking-tight text-[#111111]">
          {service.title}
        </h3>
        <p className="mt-3 font-body text-neutral-600 leading-relaxed">
          {service.summary}
        </p>
        <span className="mt-auto pt-6 inline-flex items-center gap-1.5 font-mono-tech text-xs uppercase tracking-[0.15em] text-[#2f4bff]">
          Explore
          <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  );
};

// Asymmetrical bento preview of services on the home page.
export const ServicesBento = () => {
  const [modernization, blazor, api, azure, database, reporting, aiAutomation] =
    SERVICES;

  return (
    <section
      id="work"
      data-testid="services-bento-section"
      className="relative z-10 py-24 md:py-32 bg-[#f4f3ee]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#2f4bff]">
                / What I do — my expertise
              </span>
              <h2 className="mt-5 font-head text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight max-w-2xl leading-[1.02] text-[#111111]">
                Work items, engineered end to end.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="bento-view-all"
              className="group inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.15em] text-[#111111] border border-black/20 px-6 py-3 hover:border-[#2f4bff] hover:text-[#2f4bff] transition-colors whitespace-nowrap"
            >
              View all work
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(260px,auto)]">
          <Reveal className="md:col-span-8" delay={0}>
            <Card
              service={modernization}
              testid="bento-card-modernization"
              className="h-full"
            />
          </Reveal>

          <Reveal className="md:col-span-4" delay={0.05}>
            <div
              className="group relative h-full overflow-hidden border border-black/10 bg-[#111111]"
              data-testid="bento-image-tile"
            >
              <img
                src={SERVICE_IMG}
                alt="Data center servers"
                className="h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#7d8bff]">
                  Azure • Cloud
                </p>
                <p className="mt-2 font-head text-xl font-semibold text-white">
                  Infrastructure that scales
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal className="md:col-span-4" delay={0.1}>
            <Card service={blazor} testid="bento-card-blazor" className="h-full" />
          </Reveal>
          <Reveal className="md:col-span-4" delay={0.15}>
            <Card service={api} testid="bento-card-api" className="h-full" />
          </Reveal>
          <Reveal className="md:col-span-4" delay={0.2}>
            <Card service={azure} testid="bento-card-azure" className="h-full" />
          </Reveal>

          <Reveal className="md:col-span-6" delay={0.1}>
            <Card
              service={database}
              testid="bento-card-database"
              className="h-full"
            />
          </Reveal>
          <Reveal className="md:col-span-6" delay={0.15}>
            <Card
              service={reporting}
              testid="bento-card-reporting"
              className="h-full"
            />
          </Reveal>

          {aiAutomation && (
            <Reveal className="md:col-span-12" delay={0.1}>
              <Link
                to="/services"
                data-testid="bento-card-ai-automation"
                className="group relative flex flex-col md:flex-row md:items-center justify-between gap-6 bg-[#111111] text-white p-8 md:p-10 hover:bg-[#2f4bff] transition-colors duration-300 overflow-hidden"
              >
                <div className="flex items-start gap-5 max-w-3xl">
                  <span className="font-mono-tech text-xs text-[#7d8bff] tracking-widest mt-1">
                    {aiAutomation.index}
                  </span>
                  <div>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#7d8bff]">
                      New capability
                    </p>
                    <h3 className="mt-2 font-head text-2xl md:text-3xl font-semibold tracking-tight">
                      {aiAutomation.title}
                    </h3>
                    <p className="mt-3 font-body text-neutral-300 leading-relaxed">
                      {aiAutomation.summary}
                    </p>
                  </div>
                </div>
                {aiAutomation.icon && (
                  <aiAutomation.icon className="h-10 w-10 text-white shrink-0 opacity-80 group-hover:scale-110 transition-transform" />
                )}
              </Link>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServicesBento;
